import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Flat shape consumed by the JobMap component. We keep this client-side type
 * intentionally — it lets the component filter by distance / city / type
 * without walking nested GraphQL response paths. The data still comes from
 * the existing `ordersPaginated` query (no Job-Map-specific backend types).
 */
export interface JobMapPin {
  /** Relay global ID of the OrderType node. */
  id: string;
  /** Raw int id for routing convenience. */
  orderId: number;
  positionTitle: string | null;
  companyName: string | null;
  address: string | null;
  latitude: number;
  longitude: number;
  status: string | null;
  orderType: string | null;
  recruiterName: string | null;
}

/**
 * Region used as the City filter source. Matches the existing
 * `locations.schema.RegionType` shape — just `id` and `title`. The
 * substring used to match addresses is derived client-side in the
 * component via `normalizeForMatch()`.
 */
export interface JobMapCity {
  id: string;
  title: string;
}

/** Static frontend constant — Order.order_type choices on the backend. */
export interface JobMapJobType {
  value: string;
  /** Default English label; localised via i18n key `JOB_MAP.ORDER_TYPE.<value>` in the template. */
  label: string;
}

export const JOB_TYPE_OPTIONS: JobMapJobType[] = [
  { value: 'STAFFING', label: 'Temp. Staffing' },
  { value: 'SELECTION', label: 'Selection only' },
  { value: 'PARTIAL_SELECTIONS', label: 'Partial Selections' },
  { value: 'JOBMATCH', label: 'Jobmatch service' },
];

interface RawOrderNode {
  id: string;
  orderType: string | null;
  status: string | null;
  company: { id: string; name: string | null } | null;
  specification: {
    id: string;
    positionTitle: string | null;
    address: string | null;
    latitude: number | null;
    longitude: number | null;
  } | null;
  recruiter: {
    id: string;
    user: { id: string; firstName: string | null; lastName: string | null } | null;
  } | null;
}

interface ActiveOrdersResponse {
  ordersPaginated: { edges: Array<{ node: RawOrderNode }> };
}

interface RegionsResponse {
  regions: JobMapCity[];
}

const ACTIVE_ORDERS_QUERY = gql`
  query JobMapActiveOrders {
    ordersPaginated(status: "CONFIRMED,IN_PROGRESS", hasCoordinates: true, first: 500) {
      edges {
        node {
          id
          orderType
          status
          company {
            id
            name
          }
          specification {
            id
            positionTitle
            address
            latitude
            longitude
          }
          recruiter {
            id
            user {
              id
              firstName
              lastName
            }
          }
        }
      }
    }
  }
`;

const REGIONS_QUERY = gql`
  query JobMapRegions {
    regions(isActive: true) {
      id
      title
    }
  }
`;

@Injectable({ providedIn: 'root' })
export class JobMapService {
  constructor(private apollo: Apollo) {}

  fetchActiveJobs(): Observable<JobMapPin[]> {
    return this.apollo
      .watchQuery<ActiveOrdersResponse>({
        query: ACTIVE_ORDERS_QUERY,
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => {
          const edges = result.data?.ordersPaginated?.edges ?? [];
          return edges
            .map((edge) => this.toPin(edge.node))
            .filter((pin): pin is JobMapPin => pin !== null);
        }),
      );
  }

  fetchCities(): Observable<JobMapCity[]> {
    return this.apollo
      .watchQuery<RegionsResponse>({
        query: REGIONS_QUERY,
        fetchPolicy: 'cache-first',
      })
      .valueChanges.pipe(map((result) => result.data?.regions ?? []));
  }

  /**
   * Collapse the nested OrderType node into the flat shape the component
   * expects. Returns null when essentials (coords) are missing — those
   * orders shouldn't end up as pins on the map.
   */
  private toPin(node: RawOrderNode): JobMapPin | null {
    const spec = node.specification;
    if (!spec || spec.latitude === null || spec.longitude === null) {
      return null;
    }
    const user = node.recruiter?.user;
    const first = (user?.firstName || '').trim();
    const last = (user?.lastName || '').trim();
    const recruiterName = [first, last].filter(Boolean).join(' ') || null;

    // Decode the relay global ID to get the numeric order id without an
    // extra round-trip. Falls back to NaN if the format ever changes.
    let orderId = NaN;
    try {
      const decoded = atob(node.id);
      const colonIdx = decoded.indexOf(':');
      orderId = colonIdx >= 0 ? parseInt(decoded.slice(colonIdx + 1), 10) : NaN;
    } catch {
      // Leave orderId as NaN; the component doesn't depend on it for rendering.
    }

    return {
      id: node.id,
      orderId,
      positionTitle: spec.positionTitle,
      companyName: node.company?.name || null,
      address: spec.address,
      latitude: Number(spec.latitude),
      longitude: Number(spec.longitude),
      status: node.status,
      orderType: node.orderType,
      recruiterName,
    };
  }
}
